import { sortBy, uniqBy } from 'lodash';
import moment from 'moment/moment';

import {
  EmployeeCalendarEvent,
  EmployeeCalendarPersonWithLegalEntityId,
  getEmployeeCalendarPersonsFromEvents,
} from '~/components/EmployeeCalendar';
import { AbsenceStatus, AbsenceType } from '~/data/absences/types';
import { Employees } from '~/data/employees';
import { AbsenceHistory, AbsencesReportData } from '~/data/reports';

export const convertAbsenceReportDataToEmployeesAndEvents = (
  reportData: AbsencesReportData[],
  getEmployeeById: (employeeId: number) => Employees | undefined,
): {
  employees: EmployeeCalendarPersonWithLegalEntityId[];
  events: EmployeeCalendarEvent[];
} => {
  const reportEmployees: EmployeeCalendarPersonWithLegalEntityId[] = [];

  const absenceEntries: EmployeeCalendarEvent[] = reportData
    .map((reportData) => {
      const {
        fEmpAbsenceShortHistory,
        fEmpAbsenceLongHistory,
        fEmpId,
        fEmpFirstName,
        fEmpLastName,
        fEmpLegalEntityId,
      } = reportData;

      const employeeData = getEmployeeById(fEmpId);

      reportEmployees.push({
        employeeId: fEmpId,
        firstName: fEmpFirstName,
        lastName: fEmpLastName,
        profilePic: employeeData?.fEmpProfPictureId,
        legalEntityId: fEmpLegalEntityId,
      });

      const getEvent = (
        absence: AbsenceHistory,
        type: AbsenceType,
      ): EmployeeCalendarEvent => {
        const {
          fEmpAbsenceEndDate,
          fEmpAbsenceStartDate,
          fEmpAbsenceUid,
          fEmpAbsenceStatus,
          fEmpAbsenceReason,
        } = absence;

        return {
          uId: fEmpAbsenceUid,
          employeeId: fEmpId,
          firstName: fEmpFirstName,
          lastName: fEmpLastName,
          profilePic: employeeData?.fEmpProfPictureId,
          eventCategory: type,
          eventCode: fEmpAbsenceReason,
          status: fEmpAbsenceStatus,
          startDate: moment(fEmpAbsenceStartDate).toDate(),
          endDate: !!fEmpAbsenceEndDate
            ? moment(fEmpAbsenceEndDate).toDate()
            : undefined,
        };
      };
      const processAbsences = (absences: AbsenceHistory[], type: AbsenceType) =>
        absences
          .filter(
            ({ fEmpAbsenceStatus }) =>
              fEmpAbsenceStatus !== AbsenceStatus.REJECTED,
          )
          .map((absence) => getEvent(absence, type));

      const shortAbsenceEvents = processAbsences(
        fEmpAbsenceShortHistory,
        AbsenceType.SHORT,
      );
      const longAbsenceEvents = processAbsences(
        fEmpAbsenceLongHistory,
        AbsenceType.LONG,
      );

      return shortAbsenceEvents.concat(longAbsenceEvents);
    })
    .flat();

  const sortedReportEmployees = sortBy(
    uniqBy(reportEmployees, (emp) => emp.employeeId),
    (emp) => emp.firstName,
  );

  return {
    employees: sortedReportEmployees,
    events: absenceEntries,
  };
};

export const resolveCountsAndAbsentEmployees = (props: {
  employees: EmployeeCalendarPersonWithLegalEntityId[];
  events: EmployeeCalendarEvent[];
}) => {
  const { employees, events } = props;

  const counts = {
    employees: employees.length,
    absences: events.length,
  };

  const absentEmployees = getEmployeeCalendarPersonsFromEvents(events);

  const hasNonAbsentEmployees = employees.length > absentEmployees.length;
  const hasAbsentEmployees = !!absentEmployees.length;

  return {
    counts,
    absentEmployees,
    hasAbsentEmployees,
    hasNonAbsentEmployees,
  };
};

export enum PersonnelGroupFilterIndex {
  PG1 = '1',
  PG2 = '2',
  PG3 = '3',
  PG4 = '4',
  PG5 = '5',
}

export type PersonnelGroupMappedData<T> = {
  [key in PersonnelGroupFilterIndex]: T;
};

export type MappedPersonnelGroupValues = PersonnelGroupMappedData<
  string | undefined
>;

export const getMappedPersonnelGroupValues = (
  entry: AbsencesReportData,
): MappedPersonnelGroupValues => {
  const {
    fEmpPersonnelGroups: {
      fEmpPersonnelGroup1,
      fEmpPersonnelGroup2,
      fEmpPersonnelGroup3,
      fEmpPersonnelGroup4,
      fEmpPersonnelGroup5,
    },
  } = entry;

  return {
    [PersonnelGroupFilterIndex.PG1]: fEmpPersonnelGroup1,
    [PersonnelGroupFilterIndex.PG2]: fEmpPersonnelGroup2,
    [PersonnelGroupFilterIndex.PG3]: fEmpPersonnelGroup3,
    [PersonnelGroupFilterIndex.PG4]: fEmpPersonnelGroup4,
    [PersonnelGroupFilterIndex.PG5]: fEmpPersonnelGroup5,
  };
};
